import React, { memo, ReactNode, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';
import styled from 'styled-components';

import { mq } from 'components/constants/breakpoints';

/**
 * Stand-alone KPI card and chart-card shells lifted out of PandaDocAnalytics
 * so the new Finance Dashboard (and any future analytics page) can reuse them
 * without a cross-page import. Identical visual tokens — copy of the original
 * styled bits — kept colocated so future redesigns touch one file.
 */

interface IKpiDelta {
  /** Numeric delta (current − previous) used to pick colour and arrow. */
  value: number;
  /** Human-readable label, e.g. «+12% vs prev» — rendered as-is. */
  label: string;
  /** If true, a negative delta is the «good» direction (e.g. expenses ↓). */
  inverse?: boolean;
  /**
   * ТЗ 0088: у показателя нет «хорошего» направления (расход на рекламу —
   * больше денег не хорошо и не плохо само по себе). Стрелка остаётся,
   * цвет — нейтральный серый, а не зелёный/красный.
   */
  neutral?: boolean;
}

interface IKpiCardProps {
  label: string;
  value: ReactNode;
  /**
   * Вторая строка под значением, мелко: например, суммы в других валютах
   * («+ 9025,00 EUR») — валюты не складываются в одно значение.
   */
  secondary?: ReactNode;
  subLabel?: ReactNode;
  accent?: string;
  delta?: IKpiDelta;
  /** Optional leading lucide icon rendered before the label (decorative). */
  icon?: LucideIcon;
  /** Phase 13: optional click handler — turns the card into a deep-link
   *  to the matching list view (e.g. «Reconcile queue» → /finances/invoices). */
  onClick?: () => void;
  /**
   * ТЗ 0103: высота подписи в строках — в ряду плиток, где одна подпись
   * переносится, значения остальных не уезжают выше (ряд выровнен).
   */
  labelLines?: number;
  /**
   * ТЗ 0103: плитка — ссылка (средний клик — в новую вкладку): «Лиды за 7
   * дней» ведёт в «Лиды и продажи» за те же 7 дней.
   */
  to?: { pathname: string; search?: string } | string;
  /** Подпись ссылки для чтеца (куда ведёт). */
  linkLabel?: string;
}

// Серый дельты — не светлее #64748b: 4,8:1 к белому на 11 px (WCAG AA).
const deltaColor = (value: number, inverse?: boolean, neutral?: boolean) => {
  if (value === 0 || neutral) return 'var(--k-fg-64748b)';
  const good = inverse ? value < 0 : value > 0;
  // Тёмные зелёный и красный — ≥ 4,5:1 к белому на 11 px.
  return good ? 'var(--k-fg-15803d)' : 'var(--k-fg-dc2626)';
};

// Без изменения — без стрелки и без точки: «· без изменений» читалось
// значком, которого нет.
const deltaArrow = (value: number) => {
  if (value === 0) return '';
  return value > 0 ? '↑' : '↓';
};

export const KpiCard = memo(
  ({
    label,
    value,
    secondary,
    subLabel,
    accent,
    delta,
    icon: Icon,
    onClick,
    labelLines,
    to,
    linkLabel,
  }: IKpiCardProps) => {
    const body = (
      <>
        <KpiLabel lines={labelLines}>
          {Icon ? <Icon size={12} strokeWidth={2} /> : null}
          {label}
        </KpiLabel>
        <KpiValue accent={accent}>{value}</KpiValue>
        {secondary !== undefined && <KpiSecondary>{secondary}</KpiSecondary>}
        {delta !== undefined && (
          <KpiDelta
            color={deltaColor(delta.value, delta.inverse, delta.neutral)}
          >
            {deltaArrow(delta.value)
              ? `${deltaArrow(delta.value)} ${delta.label}`
              : delta.label}
          </KpiDelta>
        )}
        {subLabel !== undefined && <KpiSubLabel>{subLabel}</KpiSubLabel>}
      </>
    );
    if (to) {
      return (
        <KpiCardLink to={to} aria-label={linkLabel}>
          {body}
        </KpiCardLink>
      );
    }
    return onClick ? (
      <KpiCardButton type="button" onClick={onClick} clickable>
        {body}
      </KpiCardButton>
    ) : (
      <KpiCardBox>{body}</KpiCardBox>
    );
  },
);

/**
 * ТЗ 0103: те же данные, что на графике, — маленькой таблицей (для чтеца
 * экрана и для тех, кому нужны точные числа). Подписи — пропсами: в
 * молекуле нет ключей перевода.
 */
export interface IChartCardTable {
  /** Подписи столбцов; первый — подпись строки. */
  columns: ReadonlyArray<string>;
  /** Строки: первая ячейка — подпись, остальные — значения. */
  rows: ReadonlyArray<ReadonlyArray<ReactNode>>;
  /** «Показать таблицей». */
  showLabel: string;
  /** «Скрыть таблицу». */
  hideLabel: string;
}

let chartTableSeq = 0;

/**
 * Переключатель «Показать таблицей»: спрятан, пока на него не пришёл фокус
 * с клавиатуры (зрячему с мышью график и так читается), раскрыт — виден и
 * под ним таблица с теми же числами.
 */
const ChartTableToggle = memo(
  ({ table, title }: { table: IChartCardTable; title: string }) => {
    const [open, setOpen] = useState(false);
    const id = useRef(`chart-table-${(chartTableSeq += 1)}`).current;
    return (
      <>
        <TableToggle
          type="button"
          $open={open}
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen((current) => !current)}
        >
          {open ? table.hideLabel : table.showLabel}
        </TableToggle>
        {open && (
          <TableWrap id={id}>
            <DataTable>
              <caption>{title}</caption>
              <thead>
                <tr>
                  {table.columns.map((column) => (
                    <th key={column} scope="col">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, rowIndex) => (
                  // Строки графика — по месту на оси: ключ — номер строки.
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) =>
                      cellIndex === 0 ? (
                        // eslint-disable-next-line react/no-array-index-key
                        <th key={cellIndex} scope="row">
                          {cell}
                        </th>
                      ) : (
                        // eslint-disable-next-line react/no-array-index-key
                        <td key={cellIndex}>{cell}</td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </TableWrap>
        )}
      </>
    );
  },
);

interface IChartCardProps {
  title: string;
  dotColor?: string;
  meta?: ReactNode;
  legend?: ReactNode;
  tall?: boolean;
  footer?: ReactNode;
  /** Optional leading lucide icon rendered before the title (decorative). */
  icon?: LucideIcon;
  /**
   * ТЗ 0103: переключатели графика в шапке карточки (метрика, «столбцами |
   * линией», лог-шкала) — справа от заголовка, переносятся на узком экране.
   */
  toolbar?: ReactNode;
  /** ТЗ 0103: своя высота области графика, px (карта объявлений — выше). */
  height?: number;
  /** ТЗ 0103: данные графика таблицей — «Показать таблицей». */
  table?: IChartCardTable | null;
  children: ReactNode;
}

export const ChartCard = memo(
  ({
    title,
    dotColor,
    meta,
    legend,
    tall,
    footer,
    icon: Icon,
    toolbar,
    height,
    table,
    children,
  }: IChartCardProps) => (
    <Card>
      <CardHeader>
        <CardTitle>
          {Icon ? <Icon size={14} strokeWidth={2} /> : null}
          {dotColor && <Dot color={dotColor} />}
          {title}
        </CardTitle>
        {legend}
        {toolbar !== undefined && <CardToolbar>{toolbar}</CardToolbar>}
        {meta !== undefined && <CardMeta>{meta}</CardMeta>}
      </CardHeader>
      <ChartWrap tall={tall} height={height}>
        {children}
      </ChartWrap>
      {footer !== undefined && <FooterNote>{footer}</FooterNote>}
      {table && table.rows.length > 0 && (
        <ChartTableToggle table={table} title={title} />
      )}
    </Card>
  ),
);

/* ----- visual tokens (kept in-file to keep this molecule standalone) ----- */

const KpiCardBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: var(--k-bg-ffffff);
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
  padding: 12px 14px;
`;

/** Same visuals as `KpiCardBox` but renders as a native button so the card
 *  can be clicked into a related list view (Phase 13 «Reconcile queue» KPI). */
const KpiCardButton = styled.button<{ clickable?: boolean }>`
  /* Кнопка по умолчанию центрирует содержимое по вертикали — в ряду с
     обычными плитками значение «съезжало» вниз. Колонка сверху вниз. */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  background: var(--k-bg-ffffff);
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
  padding: 12px 14px;
  text-align: left;
  width: 100%;
  font: inherit;
  color: inherit;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition: border-color 0.12s, box-shadow 0.12s;

  &:hover {
    ${({ clickable }) =>
      clickable
        ? 'border-color: var(--k-border-cbd5f5); box-shadow: 0 1px 4px var(--k-shadow-0f172a-a60);'
        : ''}
  }
`;

/** Плитка-ссылка: вид плитки, наведение — как у плитки-кнопки. */
const KpiCardLink = styled(Link)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: var(--k-bg-ffffff);
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
  padding: 12px 14px;
  color: inherit;
  transition: border-color 0.12s, box-shadow 0.12s;

  &:hover,
  &:focus-visible {
    color: inherit;
    border-color: var(--k-border-cbd5f5);
    box-shadow: 0 1px 4px var(--k-shadow-0f172a-a60);
  }

  &:focus-visible {
    outline: 2px solid var(--k-border-2563eb);
    outline-offset: 2px;
  }
`;

const KpiLabel = styled.div<{ lines?: number }>`
  display: flex;
  align-items: ${({ lines }) => (lines && lines > 1 ? 'flex-start' : 'center')};
  /* Строка подписи — по межстрочному интервалу antd (1.5715). */
  min-height: ${({ lines }) => (lines && lines > 1 ? `${lines * 1.58}em` : 0)};
  gap: 6px;
  color: var(--k-fg-64748b);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const KpiValue = styled.div<{ accent?: string }>`
  font-size: 22px;
  font-weight: 700;
  margin-top: 4px;
  color: ${({ accent }) => accent || 'var(--k-fg-0f172a)'};

  /* ТЗ 0121: в половине экрана телефона «−1 329 676 PLN» в 22 px не
     помещается — меньше кегль и перенос внутри числа вместо вылета. */
  ${mq.mobile} {
    font-size: 18px;
    overflow-wrap: anywhere;
  }
`;

const KpiSecondary = styled.div`
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: var(--k-fg-475569);
  font-variant-numeric: tabular-nums;
`;

const KpiSubLabel = styled.div`
  color: var(--k-fg-5b6775);
  font-size: 11px;
  margin-top: 2px;
`;

const KpiDelta = styled.div<{ color: string }>`
  margin-top: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ color }) => color};
  letter-spacing: -0.01em;
`;

const Card = styled.div`
  position: relative;
  min-width: 0;
  background: var(--k-bg-ffffff);
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;

  ${mq.mobile} {
    padding: 12px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 12px;
`;

const CardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--k-fg-0f172a);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  display: inline-block;
  background: ${({ color }) => color};
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: var(--k-fg-5b6775);
  font-variant-numeric: tabular-nums;
`;

const CardToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
`;

const ChartWrap = styled.div<{ tall?: boolean; height?: number }>`
  position: relative;
  height: ${({ tall, height }) => {
    if (height) return `${height}px`;
    return tall ? '240px' : '200px';
  }};
`;

const FooterNote = styled.div`
  font-size: 11px;
  color: var(--k-fg-5b6775);
  margin-top: 8px;
`;

/** «Показать таблицей»: невидим, пока нет фокуса; раскрыт — виден. */
const TableToggle = styled.button<{ $open: boolean }>`
  display: inline-flex;
  margin-top: 8px;
  border: 0;
  background: transparent;
  padding: 2px 0;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: var(--k-fg-2563eb);
  cursor: pointer;

  ${({ $open }) =>
    $open
      ? ''
      : `
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;

    &:focus,
    &:focus-visible {
      position: static;
      width: auto;
      height: auto;
      margin: 8px 0 0;
      overflow: visible;
      clip: auto;
    }
  `}

  &:focus-visible {
    outline: 2px solid var(--k-border-2563eb);
    outline-offset: 2px;
  }
`;

const TableWrap = styled.div`
  width: 100%;
  max-height: 320px;
  overflow: auto;
  margin-top: 8px;
  border: 1px solid var(--k-border-e2e8f0);
  border-radius: 8px;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  font-variant-numeric: tabular-nums;

  caption {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  th,
  td {
    padding: 6px 10px;
    border-bottom: 1px solid var(--k-border-f1f5f9);
    text-align: right;
    white-space: nowrap;
  }

  thead th {
    position: sticky;
    top: 0;
    background: var(--k-bg-f8fafc);
    font-size: 11px;
    font-weight: 600;
    color: var(--k-fg-64748b);
  }

  th:first-child {
    text-align: left;
    font-weight: 500;
    color: var(--k-fg-0f172a);
    white-space: normal;
  }

  thead th:first-child {
    color: var(--k-fg-64748b);
  }
`;

export const KpiGrid = styled.div<{ columns: number }>`
  display: grid;
  gap: 10px;
  margin-bottom: 10px;
  grid-template-columns: repeat(${({ columns }) => columns}, 1fr);

  /* ТЗ 0121: «minmax(0, 1fr)» — длинная сумма в плитке не раздвигает
     колонку шире экрана (у «1fr» минимум — ширина содержимого). */
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${mq.mobile} {
    gap: 8px;
  }
`;

/**
 * Сетка карточек-графиков. `columns` — сколько карточек в ряд на широком
 * экране (по умолчанию три): если роли видна одна карточка, она не должна
 * стоять узкой колонкой в пустом ряду.
 */
export const ChartsGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns ?? 3}, 1fr);
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
